import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link2, Copy, Ban, Plus } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  activo: "bg-emerald-100 text-emerald-700",
  caducado: "bg-amber-100 text-amber-700",
  revocado: "bg-rose-100 text-rose-700",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const DemoLinksSection = () => {
  const [links, setLinks] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = () => {
    api.get("/demo-links")
      .then((r) => setLinks(r.data || []))
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, []);

  const urlFor = (l) => `${window.location.origin}/demo/${l.token}`;

  const generate = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/demo-links");
      load();
      await navigator.clipboard?.writeText(`${window.location.origin}/demo/${data.token}`);
      toast.success("Enlace generado y copiado al portapapeles");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const copy = (l) => {
    navigator.clipboard?.writeText(urlFor(l));
    toast.success("Enlace copiado");
  };

  const revoke = async (l) => {
    if (!window.confirm("¿Revocar este enlace? Quien lo tenga dejará de poder entrar.")) return;
    try {
      await api.delete(`/demo-links/${l.id}`);
      toast.success("Enlace revocado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="mt-8" data-testid="demo-links-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-purple-700" />
          <h2 className="text-xl font-black tracking-tight" style={{ fontFamily: "Nunito" }}>
            Enlaces de demostración
          </h2>
        </div>
        <Button
          onClick={generate}
          disabled={creating}
          className="bg-purple-700 hover:bg-purple-800 text-white"
          data-testid="demo-link-new-btn"
        >
          <Plus className="h-4 w-4 mr-1.5" /> {creating ? "Generando..." : "Generar enlace"}
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-3">
        Comparte un enlace y quien lo abra entrará directamente en <b>modo solo lectura</b>,
        sin usuario ni contraseña. Cada enlace <b>caduca a los 7 días</b> y puedes revocarlo cuando quieras.
      </p>

      {links.length === 0 ? (
        <Card className="border-dashed border-slate-300">
          <CardContent className="py-8 text-center text-slate-400 italic text-sm">
            Aún no hay enlaces. Genera el primero con el botón de arriba.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
          {links.map((l) => (
            <Card key={l.id} className="border-slate-200" data-testid={`demo-link-card-${l.id}`}>
              <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs text-slate-700 truncate" data-testid={`demo-link-url-${l.id}`}>
                    {urlFor(l)}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${STATUS_BADGE[l.status] || "bg-slate-100 text-slate-500"}`} data-testid={`demo-link-status-${l.id}`}>
                      {l.status}
                    </span>
                    <span className="text-xs text-slate-500">Caduca: {fmtDate(l.expires_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => copy(l)} data-testid={`demo-link-copy-${l.id}`}>
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copiar
                  </Button>
                  {l.status === "activo" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-rose-700 border-rose-200 hover:bg-rose-50"
                      onClick={() => revoke(l)}
                      data-testid={`demo-link-revoke-${l.id}`}
                    >
                      <Ban className="h-3.5 w-3.5 mr-1.5" /> Revocar
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DemoLinksSection;
